import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { User, Group } from "../services/groupUser.api";

export interface GroupUserState {
  groups: Group[];
  users: User[];
  loading: boolean;
  error?: string;
  selectedGroupId?: string;
}

const initialState: GroupUserState = {
  groups: [],
  users: [],
  loading: false,
  error: undefined,
  selectedGroupId: undefined,
};

const groupUserSlice = createSlice({
  name: "groupUser",
  initialState,
  reducers: {
    // Fetch Groups
    fetchGroups: (state) => {
      state.loading = true;
      state.error = undefined;
    },
    fetchGroupsSuccess: (state, action: PayloadAction<Group[]>) => {
      state.loading = false;
      state.groups = action.payload;
      state.error = undefined;
    },
    fetchGroupsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Fetch Users
    fetchUsers: (state) => {
      state.loading = true;
      state.error = undefined;
    },
    fetchUsersSuccess: (state, action: PayloadAction<User[]>) => {
      state.loading = false;
      state.users = action.payload;
      state.error = undefined;
    },
    fetchUsersFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Create Group
    createGroup: (
      state,
      action: PayloadAction<{ name: string; memberIds: string[] }>
    ) => {
      state.loading = true;
      state.error = undefined;
    },
    createGroupSuccess: (state, action: PayloadAction<Group>) => {
      state.loading = false;
      state.groups.push(action.payload);
      state.error = undefined;
    },
    createGroupFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Add Members to Group
    addMembersToGroup: (
      state,
      action: PayloadAction<{ groupId: string; memberIds: string[] }>
    ) => {
      state.loading = true;
      state.error = undefined;
    },
    addMembersToGroupSuccess: (state, action: PayloadAction<Group>) => {
      state.loading = false;
      const index = state.groups.findIndex((g) => g.id === action.payload.id);
      if (index !== -1) {
        state.groups[index] = action.payload;
      }
      state.error = undefined;
    },
    addMembersToGroupFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Delete Group
    deleteGroup: (state, action: PayloadAction<string>) => {
      state.loading = true;
      state.error = undefined;
    },
    deleteGroupSuccess: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.groups = state.groups.filter((g) => g.id !== action.payload);
      state.error = undefined;
    },
    deleteGroupFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Remove Member from Group
    removeMemberFromGroup: (
      state,
      action: PayloadAction<{ groupId: string; memberId: string }>
    ) => {
      state.loading = true;
      state.error = undefined;
    },
    removeMemberFromGroupSuccess: (state, action: PayloadAction<Group>) => {
      state.loading = false;
      const index = state.groups.findIndex((g) => g.id === action.payload.id);
      if (index !== -1) {
        state.groups[index] = action.payload;
      }
      state.error = undefined;
    },
    removeMemberFromGroupFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // UI State
    setSelectedGroupId: (state, action: PayloadAction<string | undefined>) => {
      state.selectedGroupId = action.payload;
    },
    clearError: (state) => {
      state.error = undefined;
    },
  },
});

export const groupUserActions = groupUserSlice.actions;
export default groupUserSlice.reducer;
